import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { QoeDetail, QoeSourceAccount } from '../../../../../data/qoeApi'
import { QoeHelp } from '../QoeHelp'
import type { QoeHelpEntry, QoeHelpId } from '../qoeHelpContent'
import { loadQoeHelpContent, loadedQoeHelpContent } from '../qoeHelpStore'
import { buildQoeFacts, qoeFact } from './qoeFacts'
import { createQoeFindingPresenter } from './qoeFindingPresenter'
import { qoeOwnerText, type QoeDensity } from './qoeOwnerLanguage'
import { QoeStepCard } from './QoeStepCard'
import {
  deriveSteps, reachableTabs, QOE_CONTROL_LAYERS, QOE_STATUS_LABEL, QOE_WORKSPACE_TABS,
  type QoeStageId, type QoeStageStatus, type QoeWorkspaceTab,
} from './qoeStages'
import './QoeGuide.css'

/**
 * The guided front door: the same engagement, the same `calculate()`, the same server rules, read at
 * one of three densities.
 *
 *   Guided     — the owner. One step at a time, plain language, the real panel collapsed.
 *   Working    — the capable but untrained preparer. The same step, with the real panel expanded inline.
 *   Workpapers — the practitioner. The existing eight tabs, untouched.
 *
 * Working is a DISCLOSURE STATE of the guided card, not a third code path, and the panel it expands
 * is the element `CfoQoePage` renders on the tab. Nothing here stores a step status, a percentage or
 * a progress row: every status is derived from the analysis on each render (see `qoeStages.ts`).
 */
export type QoeMode = 'guided' | 'working' | 'workpapers'

const MODE_STORAGE_KEY = 'fg.qoe.mode'
// Three labels for increasing depth, and no two of them share a stem: "Working" and "Workpapers"
// sat next to each other differing by a suffix, and the sentence that separated them lived in a
// `title` attribute — invisible on touch and behind a hover delay everywhere else. An owner who
// means "show me more" should not be flipping a coin between a disclosure state and another page.
const MODES: ReadonlyArray<readonly [QoeMode, string, string]> = [
  ['guided', 'Guided', 'One step at a time, in plain language'],
  ['working', 'With the detail', 'The same steps with the real workpaper panels open'],
  ['workpapers', 'Workpapers', 'The eight diligence tabs'],
]
const isMode = (value: unknown): value is QoeMode => MODES.some(([id]) => id === value)

/**
 * Mode is a presentation preference, so it lives in `localStorage` and nowhere else: putting it in
 * `input` would bump the engagement revision and force a fresh review for changing how a page looks.
 * Every read and write is guarded — a private window, blocked site data or a quota error must never
 * stop the page rendering.
 */
export function readQoeMode(): QoeMode | null {
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY)
    return isMode(stored) ? stored : null
  } catch { return null }
}
export function writeQoeMode(mode: QoeMode): void {
  try { window.localStorage.setItem(MODE_STORAGE_KEY, mode) } catch { /* the preference is a convenience, never a requirement */ }
}

export function QoeModeSwitch({ mode, onChange }: { mode: QoeMode; onChange: (next: QoeMode) => void }) {
  return <div className="qoe-mode-switch">
    <div className="qoe-segmented" role="group" aria-label="How much detail to show">
      {MODES.map(([id, label, description]) => <button key={id} type="button" aria-pressed={mode === id} title={description} onClick={() => onChange(id)}>{label}</button>)}
    </div>
    {/* The description of what is selected, on the page rather than only in `title`. */}
    <small className="qoe-mode-description">{MODES.find(([id]) => id === mode)?.[2]}</small>
    {/* Always visible, at every density: a practitioner must never have to discover that the real
        workpapers exist. */}
    {mode !== 'workpapers' && <button type="button" className="qoe-text-button" onClick={() => onChange('workpapers')}>Open the full workspace →</button>}
  </div>
}

/** Owner-facing reading of the engine's readiness word. The engine's own vocabulary stays on the tabs. */
const READINESS_COPY: Record<string, string> = {
  blocked: 'Something in the numbers does not add up yet. A buyer’s accountant checks the same things, so this comes before anything else here means much.',
  qualified: 'The numbers hold together. Some of them carry a caveat a buyer will read — every one is on a step below.',
  ready_for_review: 'Everything checked agrees. What is left is having someone independent check it and publishing the result.',
}

export interface QoeGuideProps {
  detail: QoeDetail
  /** `guided` or `working`; `workpapers` renders the tabs instead of this component. */
  density: Exclude<QoeMode, 'workpapers'>
  mode: QoeMode
  onModeChange: (next: QoeMode) => void
  /** Switches to the workpapers and opens that tab — the per-step deep link. */
  onOpenWorkspace: (tab: QoeWorkspaceTab) => void
  /** The engagement's chart of accounts, used to name accounts in finding text. Best effort. */
  loadAccounts: () => Promise<{ accounts: QoeSourceAccount[] }>
  /** The real workpaper panel for a step — the identical element the tab renders. */
  renderStagePanel: (stage: QoeStageId) => ReactNode
  /** `POST /engagements/:id/demo`; absent without edit access. */
  onDemo?: () => void
  onDownloadDatabook: (packageId: string) => void
  disabled: boolean
}

export function QoeGuide({ detail, density, mode, onModeChange, onOpenWorkspace, loadAccounts, renderStagePanel, onDemo, onDownloadDatabook, disabled }: QoeGuideProps) {
  const [selected, setSelected] = useState<QoeStageId | null>(null)
  const [details, setDetails] = useState<Partial<Record<QoeStageId, boolean>>>({})
  const [accounts, setAccounts] = useState<QoeSourceAccount[]>([])
  const [help, setHelp] = useState<Record<QoeHelpId, QoeHelpEntry> | null>(loadedQoeHelpContent)
  const [demoConsent, setDemoConsent] = useState(false)

  // The guided cards render the verified help entries, so the content that the "?" popovers load on
  // demand is needed up front here. A failure leaves the titles and why-lines, which stand alone.
  useEffect(() => {
    if (help) return
    let cancelled = false
    loadQoeHelpContent().then(content => { if (!cancelled) setHelp(content) }, () => { /* the step still reads without it */ })
    return () => { cancelled = true }
  }, [help])

  // Account names make finding text readable ("Operating checking (account 1020)" rather than
  // "1020"). Without them the presenter falls back to the account code, so this never blocks a render.
  useEffect(() => {
    let cancelled = false
    loadAccounts().then(result => { if (!cancelled) setAccounts(result.accounts || []) }, () => { if (!cancelled) setAccounts([]) })
    return () => { cancelled = true }
  }, [loadAccounts])

  const state = useMemo(() => deriveSteps(detail), [detail])
  const facts = useMemo(() => buildQoeFacts(detail), [detail])
  const presenter = useMemo(
    () => createQoeFindingPresenter({ input: detail.input, analysis: detail.analysis, accounts }),
    [detail.input, detail.analysis, accounts],
  )
  const owner = (value: string) => qoeOwnerText(value, density)
  const open = selected || state.next?.stage.id || state.steps[0].stage.id
  const reachable = useMemo(() => reachableTabs(state), [state])
  const empty = detail.input.source.manifests.length === 0
  const latestPackage = detail.packages.length ? detail.packages.reduce((newest, p) => (p.revision > newest.revision ? p : newest)) : null

  return <div className={`qoe-guide qoe-guide-${density}`}>
    <QoeModeSwitch mode={mode} onChange={onModeChange} />

    <section className="qoe-card qoe-guide-hero">
      {/* Not "Investor readiness" again: the crumb and the page eyebrow already say it, and a third
          copy of the same words above the company name reads as a heading with nothing under it. */}
      <span className="qoe-kicker">Where this stands<QoeHelp id="workspace.lifecycle" /></span>
      <h2>{detail.engagement.name}</h2>
      <p>{READINESS_COPY[detail.analysis.readiness] || 'This engagement is being calculated.'}</p>
      <dl className="qoe-facts">
        <dt>Months a buyer would price</dt><dd>{qoeFact(facts, 'earnings_window', 'Not chosen yet')}</dd>
        <dt>What those months earned, before adjustments</dt><dd>{qoeFact(facts, 'reported_earnings', 'Not calculable yet')}</dd>
        <dt>After the one-offs and owner items</dt><dd>{qoeFact(facts, 'adjusted_earnings', 'Not calculable yet')}</dd>
        <dt>Checks that agree</dt><dd>{qoeFact(facts, 'checks_reconciled', 'None run yet')}</dd>
      </dl>
      {/* Both earnings figures are EBITDA. Calling it "what those months earned" with no qualifier
          anywhere on the page is the misuse path: an owner told their business earned $1.38m, shown
          a multiple in the next nav item, will do the arithmetic. The workpapers say "Reported
          EBITDA" and are honest about it; guided has to explain rather than drop the qualification. */}
      <p className="qoe-note">Those two earnings figures are before interest, tax and the cost of wearing out equipment — the measure a buyer starts from, not your profit.<QoeHelp id="overview.facts" /></p>
      <p className="qoe-guide-progress">
        <strong>{state.done} of {state.total} steps done.</strong>{' '}
        {/* "Next" implies a sequence the reader has not reached yet, and the first open step is
            usually one they passed: being pointed at step 2 after finishing six later ones reads as
            being sent back to the beginning. "Still open" is true either way, and the count says
            what is actually left there. */}
        {state.next
          ? <>Still open: <button type="button" className="qoe-text-button" onClick={() => setSelected(state.next!.stage.id)}>{owner(state.next.stage.title)}</button>
            {state.next.findings.length > 0 && <> — {state.next.findings.length === 1 ? 'one thing to look at' : `${state.next.findings.length} things to look at`}</>}</>
          : 'Every step is done for this version.'}
      </p>
    </section>

    {empty && <section className="qoe-card qoe-guide-empty">
      <span className="qoe-kicker">Nothing imported yet</span>
      <h3>Show me a finished example first<QoeHelp id="data.demo.section" /></h3>
      <p>Load an explicitly synthetic services group and walk the whole flow with numbers that already work. It is labelled synthetic everywhere it appears and must never be shown as your company’s evidence.</p>
      <label className="qoe-check">
        <input type="checkbox" checked={demoConsent} disabled={disabled || !onDemo} onChange={e => setDemoConsent(e.target.checked)} />
        <span>I want synthetic example data in this empty engagement.</span>
      </label>
      <button type="button" className="qoe-button" disabled={disabled || !onDemo || !demoConsent} onClick={() => onDemo?.()}>Show me a finished example</button>
    </section>}

    <div className="qoe-guide-columns">
      <nav className="qoe-guide-rail" aria-label="Investor readiness steps">
        <ol>
          {state.steps.map((step, i) => <li key={step.stage.id}>
            <button type="button" className={`qoe-rail-step qoe-rail-${step.status}`} aria-current={step.stage.id === open ? 'step' : undefined} onClick={() => setSelected(step.stage.id)}>
              <span className="qoe-step-index" aria-hidden="true">{i + 1}</span>
              <span>{owner(step.stage.title)}</span>
              <span className={`qoe-badge qoe-${step.status}`}>{QOE_STATUS_LABEL[step.status]}</span>
            </button>
          </li>)}
        </ol>
      </nav>

      <div className="qoe-guide-steps">
        {state.steps.map((step, i) => <QoeStepCard
          key={step.stage.id}
          step={step}
          index={i + 1}
          density={density}
          open={step.stage.id === open}
          onOpen={() => setSelected(step.stage.id === open ? null : step.stage.id)}
          detailOpen={details[step.stage.id] ?? density === 'working'}
          onToggleDetail={() => setDetails(old => ({ ...old, [step.stage.id]: !(old[step.stage.id] ?? density === 'working') }))}
          help={help?.[step.stage.helpId]}
          presentText={presenter.presentText}
          onOpenWorkspace={onOpenWorkspace}
        >{
          // The review desk is the one place in the flow where a reader commits real money to a
          // number, and it is still the practitioner form: a disposition enum, signed monthly
          // amounts and pasted evidence IDs — three of the things the plan says an owner is never
          // asked to do. Until the decision card lands it is reachable deliberately, through the
          // deep link, rather than by expanding a card.
          step.stage.id === 'review' && density === 'guided' ? null : renderStagePanel(step.stage.id)
        }</QoeStepCard>)}

        {state.unmapped.length > 0 && <section className="qoe-card">
          {/* A finding this build does not recognise is still the engine's conclusion about the
              engagement. It is shown here rather than dropped from every step. */}
          <h3>Also flagged</h3>
          <ul className="qoe-findings">
            {state.unmapped.map(finding => <li key={finding.id} className={`qoe-finding-${finding.severity}`}>
              <span className={`qoe-badge qoe-${finding.severity}`}>{finding.severity === 'error' ? 'Blocking' : 'Needs you'}</span>
              <div>{presenter.presentText(finding.message)}</div>
            </li>)}
          </ul>
        </section>}
      </div>
    </div>

    <QoeDepth detail={detail} density={density} reachable={reachable} onOpenWorkspace={onOpenWorkspace} latestPackage={latestPackage} onDownloadDatabook={onDownloadDatabook} disabled={disabled} />
  </div>
}

/**
 * What a practitioner has to be able to see in the first sixty seconds: the eight workpapers by
 * name, the nine control layers with their real status, where the databook comes from, and the
 * independence rule. Guided mode hides complexity, never the existence of the work.
 */
function QoeDepth({ detail, density, reachable, onOpenWorkspace, latestPackage, onDownloadDatabook, disabled }: {
  detail: QoeDetail
  density: Exclude<QoeMode, 'workpapers'>
  reachable: Set<QoeWorkspaceTab>
  onOpenWorkspace: (tab: QoeWorkspaceTab) => void
  latestPackage: { id: string; revision: number } | null
  onDownloadDatabook: (packageId: string) => void
  disabled: boolean
}) {
  // The most recently issued package is usually behind the draft on screen, and the button sits
  // under copy that invites handing it to someone. A bracketed version number is not a warning.
  const behind = latestPackage != null && latestPackage.revision < detail.engagement.revision
  const layers = QOE_CONTROL_LAYERS.map(layer => {
    const rows = detail.analysis.reconciliations.filter(r => r.layer === layer.id)
    const reconciled = rows.filter(r => r.status === 'reconciled').length
    const differences = rows.filter(r => r.status === 'difference').length
    const status: QoeStageStatus = !rows.length ? 'not_started' : differences > 0 ? 'blocked' : reconciled === rows.length ? 'done' : 'needs_attention'
    return { ...layer, rows: rows.length, reconciled, status }
  })
  return <section className="qoe-card qoe-guide-depth">
    <h3>What is underneath this<QoeHelp id="overview.facts" /></h3>
    <p>Every step above is a view of the same workpapers. Nothing is summarised away: the tabs, the controls and the databook are the diligence record a provider would ask for.</p>
    <div className="qoe-guide-depth-grid">
      <div>
        <h4>The eight workpapers</h4>
        <ul className="qoe-guide-tablist">
          {QOE_WORKSPACE_TABS.map(([id, label]) => <li key={id}>
            {/* Greyed when an earlier step is blocked: the numbers on that tab are not yet worth
                reading, and saying so is more honest than hiding the tab. */}
            <button type="button" className={reachable.has(id) ? '' : 'qoe-unreachable'} disabled={!reachable.has(id)} onClick={() => onOpenWorkspace(id)}>{label}</button>
          </li>)}
        </ul>
      </div>
      <div>
        <h4>The nine control layers</h4>
        <ul className="qoe-guide-layers">
          {/* Guided leads with what the control PROVES and keeps the practitioner label as the small
              print; the workpapers print the label. An owner reading "GL to TB movement" in bold with
              the meaning underneath is reading the emphasis backwards. The count gets a noun for the
              same reason: a bare "864 / 864" in a status pill reads as a status and parses as a count
              of nothing named. */}
          {layers.map(layer => <li key={layer.id}>
            {density === 'guided'
              ? <span><strong>{layer.proves}</strong><small>{layer.label}</small></span>
              : <span><strong>{layer.label}</strong><small>{layer.proves}</small></span>}
            <span className={`qoe-badge qoe-${layer.status}`}>{layer.rows ? `${layer.reconciled.toLocaleString('en-US')} of ${layer.rows.toLocaleString('en-US')} checks agree` : QOE_STATUS_LABEL.not_started}</span>
          </li>)}
        </ul>
      </div>
    </div>
    <div className="qoe-guide-depth-foot">
      <p className="qoe-note"><strong>The person who prepares a version cannot sign it off.</strong> A separate reviewer records their own acknowledgement against the exact version they read, and it is frozen into the published package.</p>
      {behind && <p className="qoe-note">You are looking at version {detail.engagement.revision}. The last published databook is version {latestPackage!.revision}, so it does not contain anything you have changed since. Publish to freeze what is on screen.</p>}
      <div className="qoe-actions">
        {latestPackage
          ? <button type="button" className="qoe-button secondary" disabled={disabled} onClick={() => onDownloadDatabook(latestPackage.id)}>{behind ? 'Download the published databook' : 'Download the databook'} (version {latestPackage.revision})</button>
          : <button type="button" className="qoe-button secondary" disabled onClick={() => undefined}>Databook — available once a version is published</button>}
        <button type="button" className="qoe-text-button" onClick={() => onOpenWorkspace('deliverables')}>See what the databook contains →</button>
      </div>
    </div>
  </section>
}
